import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Analysis,
  Template,
  User,
  Campaign,
  AggregateComments,
  ItemType,
  OverallValues,
  CampaignState,
} from "../../types";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
} from "victory";

import { useResizeDetector } from "react-resize-detector";
import { getUserName } from "../../DataTransform";
import FeedbackLoopColors from "./Colors";
import {
  IndividualTextColumns,
  getIndividualSelfPeerColumns,
  hasMultipleRaterTypes,
} from "./Columns";
import {
  tooltipFlyoutWidth,
  truncateAtWordBoundary,
  wrapLabelForTooltip,
} from "./labelHelpers";
import { HoverFlyout } from "./HoverFlyout";
import "./CellStyle.css";
import { StudentLoopResponse } from "../Results/StudentLoopResponse";
import { LoadingButton } from "@mui/lab";
import { useQuery } from "@apollo/client";
import { GET_LOOP_RESPONSES, LoopResponsesResult } from "../../Queries";
import { useUser } from "../../Contexts/UserContext";
import OverallBlock from "./blocks/OverallBlock";
import InsightSummaryBlock from "./blocks/InsightSummaryBlock";
import { OpportunitiesBlock } from "./blocks/OpportunitiesBlock";
import { GridHeaderFactory } from "./GridHeaderFactory";

function analysesToObjectArray(
  analyses: Analysis[],
  aggregates: AggregateComments[],
  sections: string[],
) {
  return sections.map((section, id) => ({
    id,
    section,
    analyses: analyses
      .filter((analysis) => analysis.question.category === section)
      .map((analysis, id) => ({
        id,
        section: analysis.question.category,
        question: analysis.question.label,
        user: getUserName(analysis.user),
        self: analysis.self,
        peers: analysis.others,
        gap: analysis.gap,
        othersInstructor: analysis.othersInstructor,
        othersStudent: analysis.othersStudent,
        othersLead: analysis.othersLead,
      })),
    aggregates: aggregates
      .filter((aggregate) => aggregate.question.category === section)
      .map((aggregate, id) => ({
        id,
        section: aggregate.question.category,
        question: aggregate.question.label,

        comments: aggregate.comments.map((comment) => ({
          ...comment,
          recipient: getUserName(comment.recipient),
          respondent: getUserName(comment.respondent),
        })),
      })),
  }));
}

export interface Props {
  title: string;
  campaign: Campaign;
  user: User;
  analyses: Analysis[];
  aggregates: AggregateComments[];
  overall: OverallValues;
  template: Template;
  resultsToken?: string;
  preview?: boolean;
  hasMixedAuthors?: boolean;
  hasMultipleRaterTypes?: boolean;
  authorType?: string;
  onAuthorTypeChange?: (v: string) => void;
  hideOverallAverages?: boolean;
}

export const AnonAnalysisView = ({
  campaign,
  user,
  analyses,
  aggregates,
  overall,
  template,
  resultsToken,
  preview = false,
  hasMixedAuthors = false,
  hasMultipleRaterTypes: hasMultipleRaterTypesProp,
  authorType = "",
  onAuthorTypeChange,
  hideOverallAverages = false,
}: Props) => {
  const studentLoopResponseId = "respond";
  // Prefer the authoritative backend-provided flag; fall back to computing
  // from template items (in case the prop is omitted, though items may be
  // filtered by visibility and miss cross-role recipient types).
  const multipleRaterTypes =
    hasMultipleRaterTypesProp ?? hasMultipleRaterTypes(template.items);
  const questions = template.items;
  const sections = questions
    .map((question) => question.category)
    .filter((category, index, array) => array.indexOf(category) === index);

  const groupCount = questions.filter(
    (question) => question.itemType === ItemType.Choice,
  ).length;

  const id = "analysis-view";
  const { width: chartWidth, ref: chartRef } =
    useResizeDetector<HTMLDivElement>();
  const safeChartWidth = chartWidth ? chartWidth : 800;

  const groupOffset = 20;
  const chartHeight = groupCount * (groupOffset + 4) * (1 + 2) + 150;
  const safeChartHeight = chartHeight && chartHeight >= 200 ? chartHeight : 200;

  const { isStudent } = useUser();

  //
  // Queries
  //

  const { data: loopResponsesData } = useQuery<LoopResponsesResult>(
    GET_LOOP_RESPONSES,
    {
      variables: { campaignId: campaign.id, subjectId: user.id },
      context: {
        headers: resultsToken
          ? {
              Authorization: `JWT ${resultsToken}`,
            }
          : undefined,
      },
    },
  );

  //
  // Scroll to respond anchor when arriving via #respond hash
  //
  useEffect(() => {
    if (window.location.hash === `#${studentLoopResponseId}`) {
      const frame = requestAnimationFrame(() => {
        document
          .getElementById(studentLoopResponseId)
          ?.scrollIntoView({ behavior: "smooth" });
      });
      return () => cancelAnimationFrame(frame);
    }
  }, []);

  //
  // Helpers
  //
  const analysisObjectArray = analysesToObjectArray(
    analyses,
    aggregates,
    sections,
  );

  // Chart axis labels: allow up to ~120 chars (truncated at word boundary).
  // Lines wrap at 20 chars; labels longer than 6 lines (~120 chars) get cut off.
  function friendlyLabel(text: string) {
    const truncated = truncateAtWordBoundary(text, 120);
    const lines = divideIntoLines(truncated, 20);
    if (lines.length > 6) lines[5] = lines[5] + "...";
    return lines.slice(0, 6);
  }

  function prependWarning(text: string, outlier: boolean | undefined) {
    return outlier ? `⚠️ ${text}` : text;
  }

  // Custom axis label component: shows a styled tooltip flyout on hover with
  // the wrapped full text, matching the chart bar/scatter tooltip style. (FL-209)
  const TooltipAxisLabel = (props: any) => {
    const [hovered, setHovered] = useState(false);
    const { datum, x = 0, y = 0 } = props;
    const idx = (typeof datum === "number" ? datum : 0) - 1;
    const a = analyses[idx];
    if (!a) return <VictoryLabel {...props} text={""} />;
    const fullText = prependWarning(a.label, a.outlier);
    const displayText = prependWarning(
      friendlyLabel(a.label) as unknown as string,
      a.outlier,
    );
    const wrappedFull = wrapLabelForTooltip(fullText, undefined, 40);
    return (
      <g
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <VictoryLabel {...props} text={displayText} />
        {hovered && <HoverFlyout x={x} y={y} lines={wrappedFull} />}
      </g>
    );
  };

  function divideIntoLines(text: string, targetLength: number) {
    const words = text.split(" ");
    const lines = [];
    let line = "";

    for (let i = 0; i < words.length; i++) {
      if (words[i].length > targetLength) {
        // split the word into chunks that fit the target length
        const chunks = chunkString(words[i], targetLength);
        for (let j = 0; j < chunks.length; j++) {
          lines.push(chunks[j]);
        }
      } else if (line.length + words[i].length + 1 <= targetLength) {
        line += words[i] + " ";
      } else {
        lines.push(line.trim());
        line = words[i] + " "; // Start new line with current word
      }
    }

    // Only push the last line if it's not empty
    if (line.trim()) {
      lines.push(line.trim());
    }

    return lines;
  }

  function chunkString(str: string, length: number) {
    const chunks = [];
    for (let i = 0; i < str.length; i += length) {
      chunks.push(str.substring(i, i + length) + "-");
    }
    chunks[chunks.length - 1] = chunks[chunks.length - 1].slice(0, -1);
    return chunks;
  }

  function handleRespond() {
    document
      .getElementById(studentLoopResponseId)
      ?.scrollIntoView({ behavior: "smooth" });
  }

  async function handleExportToPdf() {
    try {
      const resp = await fetch(
        `/api/campaigns/${campaign.id}/users/${user.id}/pdf`,
        {
          credentials: "include",
          headers: {
            Authorization: resultsToken ? `JWT ${resultsToken}` : "",
          },
        },
      );
      if (!resp.ok) {
        throw new Error(await resp.text());
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `campaign-${campaign.id}-user-${user.id}-pdf.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  }

  // Get student response from loop responses
  const studentResponse = loopResponsesData?.loopResponses?.find(
    (r) => r.loopType === "SR",
  );
  const studentHasResponded =
    studentResponse !== undefined && studentResponse.message.trim() !== "";

  return (
    <Stack spacing={4}>
      <Box sx={{ pt: 5 }}></Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <LoadingButton variant="text" onClick={handleExportToPdf}>
          Export to PDF
        </LoadingButton>
        {campaign?.loopStudent &&
          campaign?.state === CampaignState.Closed &&
          isStudent && (
            <Button
              variant="contained"
              disabled={studentHasResponded}
              onClick={handleRespond}
            >
              Respond
            </Button>
          )}
      </Box>

      {!hideOverallAverages && (
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Average Ratings</Typography>
            {hasMixedAuthors && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={authorType}
                  onChange={(e) =>
                    onAuthorTypeChange?.(e.target.value as string)
                  }
                  displayEmpty
                >
                  <MenuItem value="">All Authors</MenuItem>
                  <MenuItem value="instructor">Instructor-Authored</MenuItem>
                  <MenuItem value="peer">Peer-Authored</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
          <OverallBlock overall={overall} />
        </Stack>
      )}

      <Stack spacing={1}>
        <Typography variant="h6" sx={{ mt: 0.5 }}>
          Your Insights
        </Typography>
        <Stack spacing={2}>
          <InsightSummaryBlock analysisObjects={analysisObjectArray} />
          <OpportunitiesBlock
            analyses={analyses}
            safeChartWidth={safeChartWidth}
            user={user}
          />
        </Stack>
      </Stack>

      {aggregates.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 0.5 }}>
            Your Written Feedback
          </Typography>
          <div>
            {analysisObjectArray.map((section) =>
              section.aggregates.map((aggregate) => (
                <DataGrid
                  autoHeight={true}
                  key={aggregate.id}
                  hideFooter={
                    !aggregate.comments || aggregate.comments.length < 100
                  }
                  rows={aggregate.comments}
                  components={{
                    Toolbar: GridHeaderFactory(
                      `${section.section} - ${aggregate.question}`,
                    ),
                  }}
                  sx={{ backgroundColor: "black", mb: 1 }}
                  columns={IndividualTextColumns}
                  pagination={undefined}
                  getRowHeight={() => "auto"}
                />
              )),
            )}
          </div>
        </>
      )}

      <Stack spacing={2}>
        <Typography variant="h6" sx={{ mt: 0.5 }}>
          Full Results
        </Typography>
        <div style={{ height: safeChartHeight }} ref={chartRef}>
          {analyses.length > 0 && (
            <VictoryChart
              horizontal
              theme={VictoryTheme.material}
              padding={{ bottom: 50, top: 40, left: 100, right: 0 }}
              height={safeChartHeight}
              width={safeChartWidth}
              domain={{ x: [0.5, groupCount + 0.5], y: [0.5, 5.5] }}
            >
              <VictoryAxis
                tickValues={analyses.map((_, i) => i + 1)}
                style={{
                  axis: { stroke: "white" },
                  axisLabel: { padding: 20 },
                  grid: { stroke: "white" },
                  ticks: { stroke: "white", size: 5 },
                  tickLabels: { padding: 5, stroke: "white", fill: "white" },
                }}
                tickLabelComponent={
                  <TooltipAxisLabel angle={-45} textAnchor={"end"} />
                }
              />

              <VictoryAxis
                dependentAxis
                label="Rating"
                style={{
                  axis: { stroke: "white" },
                  axisLabel: { padding: 30 },
                  grid: { stroke: "white" },
                  ticks: { stroke: "white", size: 5 },
                  tickLabels: { padding: 5, stroke: "white", fill: "white" },
                }}
              />

              <VictoryBar
                barWidth={20}
                colorScale={FeedbackLoopColors}
                key={user.id}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea
                    orientation={"top"}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    // eslint-disable-next-line react/prop-types
                    flyoutWidth={({ datum }) => tooltipFlyoutWidth(datum?.label)}
                  />
                }
                name="bar"
                data={analyses.map((question, index) => ({
                  x: index + 1,
                  y: question.others,
                  label: wrapLabelForTooltip(
                    question.label,
                    `(Received: ${question.others?.toFixed(1)})`,
                  ),
                }))}
                style={{
                  data: {
                    stroke: "white",
                    strokeDasharray: 0,
                    strokeWidth: 2,
                    fill: FeedbackLoopColors[0],
                  },
                }}
              />

              <VictoryScatter
                key={user.id + "others"}
                style={{
                  data: {
                    fill: "white",
                  },
                }}
                size={6}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea
                    orientation={"top"}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    // eslint-disable-next-line react/prop-types
                    flyoutWidth={({ datum }) => tooltipFlyoutWidth(datum?.label)}
                  />
                }
                name="scatter"
                data={analyses.map((question, index) => ({
                  x: index + 1,
                  y: question.self,
                  label: wrapLabelForTooltip(
                    question.label,
                    `(SELF: ${question.self?.toFixed(1)})`,
                  ),
                  others: question.others,
                }))}
              />
            </VictoryChart>
          )}
        </div>

        <div id={id}>
          {analysisObjectArray.map((section) => (
            <>
              <DataGrid
                autoHeight={true}
                key={section.id}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "gap", sort: "desc" }],
                  },
                }}
                hideFooter={!section.analyses || section.analyses.length < 100}
                rows={section.analyses}
                components={{
                  Toolbar: GridHeaderFactory(section.section),
                }}
                sx={{ backgroundColor: "black", mb: 1 }}
                columns={getIndividualSelfPeerColumns(multipleRaterTypes)}
                pagination={undefined}
              />
            </>
          ))}
        </div>
      </Stack>

      {(campaign?.loopStudent || campaign?.loopInstructor) && user && (
        <Stack spacing={2}>
          <Link id={studentLoopResponseId}></Link>
          <StudentLoopResponse
            campaignId={campaign.id}
            userId={user.id}
            resultsToken={resultsToken}
            showStudent={campaign.loopStudent}
            showInstructor={campaign.loopInstructor}
            preview={preview}
          />
        </Stack>
      )}
    </Stack>
  );
};
